import React, { useCallback, useState, useMemo, useEffect } from "react";
import {
  ScrollView,
  View,
  StyleSheet,
  TouchableOpacity,
  Image,
  Text,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import { AppScreens } from "../../../../../screens/Utility/AppScreens";
import { navigateToScreen } from "../../../../../utility/NavigationHelper";
import { AppImages } from "../../../../../utility/AppImages";
import { useStreamerSocket } from "../hooks/useStreamerSocket";
import { useChatHandler } from "../../SoloStreaming/hooks/useChatHandler";

import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { ScreenBGGradientView } from "../../../../../common/CommonView";
import Separator from "../../../../../Components/Separator";
import MatchPreviewComponent from "./MatchPreviewComponent";
import { useWsProvider } from "../../../../../context/NativeApplicationContext";
import { useGoLive } from "../GoLiveContext";
import { useStreamingOAuth } from "../../../../../hooks/useStreamingOAuth";
import { AppColors } from "../../../../../utility/AppColors";
import { LinearGradient } from "expo-linear-gradient";
import LiveEventChat from "./LiveEventChat";
import LiveParticipantsChat from "./LiveParticipantsChat";
import StreamerView from "./StreamerView";
import StreamControlPanel from "./StreamControlPanel";
import { useStreamStatus } from "../../../../../context/StreamStatusContext";
import StopStreamModal from "./StopStreamModal";

type ExpandedChat = "event" | "participants" | null;

interface ModalStates {
  evidencePopup: boolean;
  greatEffortPopup: boolean;
  addSourcePopup: boolean;
  showWinnerModal: boolean;
  isShare: boolean;
  isDisputeModalVisible: boolean;
  isEventCompletedModalVisible: boolean;
  isDisputeResultModalVisible: boolean;
  isManageSourcesPopupVisible: boolean;
}

const initialModalStates: ModalStates = {
  evidencePopup: false,
  greatEffortPopup: false,
  addSourcePopup: false,
  showWinnerModal: false,
  isShare: false,
  isDisputeModalVisible: false,
  isEventCompletedModalVisible: false,
  isDisputeResultModalVisible: false,
  isManageSourcesPopupVisible: false,
};

const StreamSetup: React.FC = () => {
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const navigation = useNavigation();

  const [expandedChat, setExpandedChat] = useState<ExpandedChat>(null);
  const [isStreamStarting, setIsStreamStarting] = useState(false);
  const [modalStates, setModalStates] =
    useState<ModalStates>(initialModalStates);
  const [showStopConfirm, setShowStopConfirm] = useState(false);

  // Get stream data and actions from GoLiveContext
  const {
    streamData,
    isLive,
    isLoading,
    createAndStartStream,
    stopCurrentStream,
  } = useGoLive();

  // Get access token and active stream from StreamStatusContext
  const { accessToken, activeStream } = useStreamStatus();

  // Socket & Chat
  const { socket, currentUser } = useStreamerSocket(activeStream?.id);
  const { messages, sendMessage, likes, totalViewers, totalGiftValue } =
    useChatHandler(
      socket,
      activeStream?.id,
      currentUser?.id || "",
      currentUser?.username || "",
      currentUser?.avatarUrl
    );

  // Get streaming platform usernames
  const { twitchUsername, youtubeUsername } = useStreamingOAuth(
    accessToken || "",
    ""
  );

  // Get native app streaming state
  const {
    streamStarted,
    startRecording,
    toggleRecording,
    isNativeAppConnected,
  } = useWsProvider();

  const updateModalState = useCallback(
    (key: keyof ModalStates, value: boolean) => {
      setModalStates((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  // Toggle stream (start or stop)
  const toggleStream = useCallback(async (): Promise<boolean> => {
    if (isLive || streamStarted) {
      // Stop streaming - Show confirmation modal
      setShowStopConfirm(true);
      return false; // Prevent immediate stop
    } else {
      // Start streaming
      setIsStreamStarting(true);
      try {
        return await createAndStartStream();
      } finally {
        setIsStreamStarting(false);
      }
    }
  }, [isLive, streamStarted, createAndStartStream]);

  const handleConfirmStop = async () => {
    await stopCurrentStream();
    setShowStopConfirm(false);
    navigateToScreen(navigation, AppScreens.HomeScreen);
  };

  const handleEventChatExpand = useCallback((expand: boolean) => {
    setExpandedChat(expand ? "event" : null);
  }, []);

  const handleParticipantChatExpand = useCallback((expand: boolean) => {
    setExpandedChat(expand ? "participants" : null);
  }, []);

  const isSmallScreen = useMemo(
    () => deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM,
    [deviceSize]
  );

  const containerPadding = isSmallScreen ? 10 : 10;
  const separatorMarginTop = isSmallScreen ? 10 : 20;

  // Determine stream status for preview (map 'failed' to 'pending' for MatchPreviewComponent)
  const streamStatus = useMemo(():
    | "pending"
    | "live"
    | "paused"
    | "completed" => {
    if (isLive || streamStarted) return "live";
    if (activeStream) {
      const status = activeStream.status;
      // Map 'failed' to 'pending' as MatchPreviewComponent doesn't support 'failed'
      if (status === "failed") return "pending";
      return status;
    }
    return "pending";
  }, [isLive, streamStarted, activeStream]);

  return (
    <View style={styles.container}>
      <ScreenBGGradientView />

      <View style={{ padding: containerPadding }}>
        <TouchableOpacity
          onPress={() => navigateToScreen(navigation, AppScreens.HomeScreen)}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
          }}
        >
          <LinearGradient
            colors={["#1F2937", "#111827"]}
            start={[0, 0]}
            end={[1, 1]}
            style={{
              width: 40,
              height: 40,
              padding: 6,
              borderRadius: 8,
              borderWidth: 0.8,
              borderColor: "#374151",
              justifyContent: "center",
              alignItems: "center",
              aspectRatio: 1,
            }}
          >
            <View
              style={{
                width: "100%",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Image
                source={AppImages.icArrowLeft}
                style={{ width: 24, height: 24 }}
                resizeMode="contain"
              />
            </View>
          </LinearGradient>
          <Text style={{ color: "white", fontSize: 20, fontWeight: "700" }}>
            Stream Dashboard
          </Text>
        </TouchableOpacity>

        <Separator
          color={AppColors.black_2D2733}
          height={1}
          marginTop={separatorMarginTop}
          marginBottom={0}
          marginHorizontal={0}
        />
      </View>

      <ScrollView
        contentContainerStyle={{ flexGrow: 1 }}
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
      >
        <View style={{ padding: containerPadding, paddingTop: 0 }}>
          <View style={{ gap: 20 }}>
            <View
              style={{
                flexDirection: isSmallScreen ? "column" : "row",
                justifyContent: "space-between",
                gap: 20,
              }}
            >
              <StreamerView
                setIsShare={(value) => updateModalState("isShare", value)}
                isManageSourcesPopupVisible={
                  modalStates.isManageSourcesPopupVisible
                }
                onManageSourcesPress={() =>
                  updateModalState(
                    "isManageSourcesPopupVisible",
                    !modalStates.isManageSourcesPopupVisible
                  )
                }
                likes={likes}
                totalViewers={totalViewers}
                totalGiftValue={totalGiftValue}
              />

              <View
                style={[
                  { width: isSmallScreen ? "100%" : "25%" },
                  expandedChat === "event" ? styles.fullScreenChat : {},
                ]}
              >
                <LiveEventChat
                  setIsExpandChat={handleEventChatExpand}
                  isExpandChat={expandedChat === "event"}
                  connectedPlatforms={streamData.platforms}
                  messages={messages}
                  onSendMessage={sendMessage}
                  twitchUsername={twitchUsername}
                  youtubeVideoId={
                    activeStream?.platforms?.youtube?.liveStreamId ||
                    youtubeUsername
                  }
                />
              </View>

              <View
                style={[
                  { width: isSmallScreen ? "100%" : "25%" },
                  expandedChat === "participants" ? styles.fullScreenChat : {},
                ]}
              >
                <LiveParticipantsChat
                  setIsExpandChat={handleParticipantChatExpand}
                  isExpandChat={expandedChat === "participants"}
                />
              </View>
            </View>

            {!isSmallScreen && (
              <View style={styles.previewControlSection}>
                <View style={{ flexDirection: "column", width: "73.5%" }}>
                  <MatchPreviewComponent
                    streamStatus={streamStatus}
                    platform={
                      streamData.platforms.twitch ? "twitch" : "youtube"
                    }
                    streamId={
                      streamData.platforms.twitch
                        ? twitchUsername || undefined
                        : activeStream?.platforms?.youtube?.liveStreamId ||
                          youtubeUsername ||
                          undefined
                    }
                  />
                </View>
                <StreamControlPanel
                  startStream={streamStarted || isLive}
                  toggleStream={toggleStream}
                  startRecording={startRecording}
                  toggleRecording={toggleRecording}
                  accessToken={accessToken || ""}
                  isStreamStarting={isStreamStarting}
                  loading={isLoading}
                  updateModalState={updateModalState}
                />
              </View>
            )}
          </View>
        </View>
      </ScrollView>
      <StopStreamModal
        visible={showStopConfirm}
        onClose={() => setShowStopConfirm(false)}
        onConfirm={handleConfirmStop}
      />
    </View>
  );
};

export default StreamSetup;

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
    },
    scrollView: {
      overflow: "scroll",
    },
    fullScreenChat: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: "rgba(0, 0, 0, 0.9)",
      zIndex: 999,
    },
    previewControlSection: {
      flexDirection: "row",
      height: 300,
      justifyContent: "space-between",
    },
  },
  {
    [DEVICE_SIZES.LG]: {},
    [DEVICE_SIZES.MD]: {},
    [DEVICE_SIZES.SM]: {},
    [DEVICE_SIZES.XS]: {},
  }
);
